import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useAuthStore } from '../store/authStore'
import { POST_AUTH_REDIRECT_KEY } from '../hooks/useAuth'
import { useState, useRef } from 'react'

let Home_video = ({src = undefined, poster = undefined, brand = undefined}:
  {src?: string, poster?: string, brand?: string})=>{
  let ref = useRef<HTMLVideoElement>(null)
  let [is_play, is_play_set] = useState(false)
  return (
    <div className="group relative rounded-xl sm:rounded-2xl overflow-hidden aspect-[9/16] bg-black shadow-lg hover:shadow-2xl transition-shadow">
      <video ref={ref} src={src} poster={poster} controls={is_play}
        playsInline preload="none" className="w-full h-full object-cover"
        onPlay={()=>is_play_set(true)} onPause={()=>is_play_set(false)}
        onEnded={()=>is_play_set(false)} />
      {!is_play && (
        <button onClick={()=>ref.current?.play()} aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/50 via-black/10 to-transparent cursor-pointer">
          <span className="relative flex items-center justify-center">
            <span className="absolute w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-white/40 animate-ping" />
            <img src="/logo_sponly_color_icon.svg" alt=""
              className="relative w-14 h-14 sm:w-20 sm:h-20 drop-shadow-2xl group-hover:scale-110 transition-transform duration-300" />
          </span>
        </button>
      )}
      {brand && (
        <div className="pointer-events-none absolute top-2 sm:top-3 right-2 sm:right-3 px-2 sm:px-3 py-1 sm:py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-bold text-gray-900 shadow-lg">
          {brand}
        </div>
      )}
    </div>
  )
}

let Home_benefits = ({title = undefined, desc = undefined, items = [], cta = undefined,
  on_cta = undefined, className = ''}: {title?: string, desc?: string,
  items?: {lbl: string, desc: string, icon: string}[], cta?: string,
  on_cta?: ()=>void, className?: string})=>(
  <section className={`py-12 sm:py-16 px-4 sm:px-6 lg:px-8 ${className}`}>
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 max-w-3xl mx-auto text-balance">
          {title}
        </h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
          {desc}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
        {items.map(item=>(
          <div key={item.lbl} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#0066FF] to-[#6B4DE6] rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{item.lbl}</h3>
            <p className="text-sm sm:text-base text-gray-600">{item.desc}</p>
          </div>
        ))}
      </div>
      <div className="text-center mt-8 sm:mt-12">
        <button onClick={()=>on_cta?.()}
          className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-semibold text-sm sm:text-base hover:shadow-xl transition-all">
          {cta}
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </button>
      </div>
    </div>
  </section>
)

export default function Home() {
  const navigate = useNavigate()
  const { loginWithRedirect } = useAuth0()
  const { user } = useAuthStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handle_signup = () => {
    localStorage.setItem(POST_AUTH_REDIRECT_KEY, 'dashboard')
    loginWithRedirect({authorizationParams: {screen_hint: 'signup'}})
  }

  const handleGetStarted = () => {
    if (user) {
      const userType = user.user_type
      if (userType == 'brand')
        navigate('/brand/dashboard')
      else
        navigate('/creator/dashboard')
      return
    }
    handle_signup()
  }

  let brand_benefits = [
    {lbl: 'Stay Present', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      desc: 'Build a consistent brand presence through creator content, instead of relying solely on occasional campaigns.'},
    {lbl: 'Content That Can Keep Working', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
      desc: 'Creator content can continue attracting views after publication, creating opportunities for ongoing discovery beyond the initial campaign period.'},
    {lbl: 'Reach Relevant Audiences', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      desc: 'Connect your brand with audiences through creators whose content aligns with your marketing goals.'},
  ]
  let efficiency_benefits = [
    {lbl: 'Clearer Sponsorship Pricing', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
      desc: 'Explore sponsorship opportunities with fixed pricing by creator tier.'},
    {lbl: 'Less Complexity. More Action.', icon: 'M13 10V3L4 14h7v7l9-11h-7z',
      desc: 'Discover creators, explore opportunities, and launch sponsorship campaigns through one streamlined platform.'},
    {lbl: 'Built for Ongoing Activity', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
      desc: 'Make creator sponsorships part of your regular marketing calendar, rather than treating every activation as a standalone project.'},
  ]

  let example_videos = ['Coca-Cola', 'Adidas', 'Nike', 'Samsung'].map((brand, i)=>
    ({src: `/home_video${i+1}.mp4`, poster: `/home_video${i+1}.jpg`, brand}))

  return (
    <div className="min-h-screen bg-white">
      {}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {}
            <button
              onClick={() => navigate('/')}
              className="flex items-center"
            >
              <img src="/logo_sponly_color.svg" alt="Sponly" className="h-[28.16px] sm:h-[31.68px] w-auto" />
            </button>
            
            {}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <button
                  onClick={handleGetStarted}
                  className="px-6 py-2 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-6 py-2 text-gray-700 hover:text-gray-900 font-semibold transition"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => handle_signup()}
                    className="px-6 py-2 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>

            {}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              {user ? (
                <button
                  onClick={() => {
                    handleGetStarted();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-semibold"
                >
                  Dashboard
                </button>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 text-gray-700 font-semibold text-left hover:bg-gray-50 rounded-lg"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => {
                      handle_signup();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-semibold"
                  >
                    Get Started
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3 sm:px-4 py-2 shadow-sm border border-gray-200 mb-6">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-xs sm:text-sm font-semibold text-gray-700">AI-POWERED CREATOR SPONSORSHIPS</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight max-w-4xl mx-auto">
            Make Every Month a
            <span className="block mt-2 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] bg-clip-text text-transparent">
              Brand-Building Opportunity.
            </span>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            Turn creator content into an ongoing brand presence. Launch sponsorships efficiently, connect with relevant audiences, and make creator marketing a consistent part of your growth strategy.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <button
              onClick={() => handle_signup()}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-bold text-base sm:text-lg hover:shadow-xl transition-all"
            >
              Launch Your First Campaign
            </button>
            <button
              onClick={() => handle_signup()}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-bold text-base sm:text-lg hover:bg-gray-50 transition-all"
            >
              I'm a Creator
            </button>
          </div>
        </div>
      </section>

      {}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 text-balance">
              Your Brand, Inside the Content People Love.
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Discover how brands can integrate into creator content through sponsorships designed to fit naturally into the content people already watch.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
            {example_videos.map((video, idx) => (
              <Home_video key={idx} src={video.src} poster={video.poster}
                brand={video.brand} />
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <button
              onClick={() => handle_signup()}
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-semibold text-sm sm:text-base hover:shadow-xl transition-all"
            >
              Create Your First Campaign
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] bg-clip-text text-transparent mb-2">500M+</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Monthly Views</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] bg-clip-text text-transparent mb-2">10K+</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Active Creators</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] bg-clip-text text-transparent mb-2">500+</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Brands</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] bg-clip-text text-transparent mb-2">98%</div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {}
      <Home_benefits title="One Campaign Is a Start. Consistency Builds Brands."
        desc="Building a strong brand takes more than occasional bursts of advertising. SPONLY helps brands make creator sponsorships an ongoing part of their marketing mix, creating more opportunities to connect with relevant audiences over time."
        items={brand_benefits} cta="Build Your Brand Presence" on_cta={handle_signup}
        className="bg-white" />

      {}
      <Home_benefits title="Make Creator Sponsorships Work for Your Budget."
        desc="Brand-building shouldn't have to wait for your next big campaign. SPONLY gives marketers a streamlined way to discover sponsorship opportunities and plan creator activations around their marketing objectives and available budgets."
        items={efficiency_benefits} cta="Explore Sponsorship Opportunities"
        on_cta={handle_signup}
        className="bg-gradient-to-br from-blue-50 via-white to-purple-50" />

      {}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              Launch creator sponsorship campaigns through a clear, straightforward process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
            {}
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl border border-blue-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#0066FF] to-[#6B4DE6] rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">For Brands</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                Plan and launch creator sponsorships around your brand goals and budget.
              </p>
              
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700">Set your campaign goals, audience, and budget.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700">Discover creators and sponsorship opportunities.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700">Build an ongoing brand presence through creator content.</p>
                </div>
              </div>

              <button
                onClick={() => handle_signup()}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg transition-all"
              >
                Launch Your First Campaign →
              </button>
            </div>

            {}
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl border border-purple-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#6B4DE6] to-[#0066FF] rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">For Creators</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                Turn your content into sponsorship opportunities with brands.
              </p>
              
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700">Upload your content.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700">Discover relevant brand campaigns.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700">Complete sponsorships and get paid.</p>
                </div>
              </div>

              <button
                onClick={() => handle_signup()}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#6B4DE6] to-[#0066FF] text-white rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg transition-all"
              >
                Explore Brand Campaigns →
              </button>
            </div>
          </div>
        </div>
      </section>

      {}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-balance">
            Ready to Make Creator Sponsorships Part of Your Growth Strategy?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 opacity-90 max-w-2xl mx-auto">
            Start exploring creator sponsorships and discover a more streamlined way to build your brand presence over time.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={() => handle_signup()}
              className="w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-5 bg-white text-[#0066FF] rounded-xl font-bold text-base sm:text-lg hover:shadow-2xl transition-all"
            >
              Launch Your First Campaign
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-5 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-xl font-bold text-base sm:text-lg hover:bg-white/20 transition-all"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {}
      <footer className="bg-gray-900 text-white py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            {}
            <div className="flex items-center">
              <img src="/logo_sponly_color.svg" alt="Sponly"
                className="h-[31.68px] w-auto brightness-0 invert" />
            </div>
            
            {}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm">
              <a href="/legal/privacy" className="text-gray-400 hover:text-white transition">Privacy Policy</a>
              <a href="/legal/terms" className="text-gray-400 hover:text-white transition">Terms of Service</a>
              <a href="/legal/copyright" className="text-gray-400 hover:text-white transition">Copyright Policy</a>
              <a href="mailto:info@sponly.ai" className="text-gray-400 hover:text-white transition">Contact</a>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-6 text-center text-sm text-gray-400">
            © 2026 Sponly.ai. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}